import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { listOrders, notifyShipment } from "@/api/endpoints";
import { ApiError } from "@/api/client";
import type { FulfillmentResponse, OrderDto } from "@/api/types";

// 発送完了の対象は常に allocated (実績取込済 = 物理発送できる状態)。
// printed は本フローで到達せず、label_ready は発送前のため対象にしない。
const FULFILL_STATUS = "allocated";

export default function FulfillmentPage() {
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [result, setResult] = useState<FulfillmentResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  // 2026-06-12 拡張オプション (Kota さん要望):
  // - skipShopeeNotify: プラットフォーム notify を呼ばず Wonect 側 status だけ SHIPPED
  //   化 (在庫入荷後の B ケース: 先に Shopee 側 shipped 化済みの注文の物理発送時)
  // - includeShopeeTWUnallocated: Shopee TW で status != SHIPPED の全 Order を ECMS
  //   先行発番 → Shopee 側 shipped 化 (Wonect status は維持)。Shopee キャンセル防止用
  const [skipShopeeNotify, setSkipShopeeNotify] = useState(false);
  const [includeShopeeTWUnallocated, setIncludeShopeeTWUnallocated] = useState(false);
  const queryClient = useQueryClient();

  const ordersQ = useQuery({
    queryKey: ["orders", "fulfillment", FULFILL_STATUS],
    queryFn: () => listOrders({ status: FULFILL_STATUS, limit: 200 }),
  });

  const mut = useMutation({
    mutationFn: () =>
      notifyShipment({
        order_ids: Array.from(selected),
        dry_run: false,
        skip_shopee_notify: skipShopeeNotify,
        include_shopee_tw_unallocated: includeShopeeTWUnallocated,
      }),
    onSuccess: (data) => {
      setResult(data);
      setError(null);
      queryClient.invalidateQueries({ queryKey: ["orders"] });
      queryClient.invalidateQueries({ queryKey: ["dashboard"] });
      setSelected(new Set());
    },
    onError: (e) => {
      setError(e instanceof ApiError ? e.message : String(e));
      setResult(null);
    },
  });

  const toggle = (id: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleAll = () => {
    if (!ordersQ.data) return;
    const allIds = ordersQ.data.orders
      .filter((o) => o.tracking_no)
      .map((o) => o.id);
    if (selected.size === allIds.length) {
      setSelected(new Set());
    } else {
      setSelected(new Set(allIds));
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-brand-primary">発送完了</h2>
        <p className="text-sm text-gray-500 mt-1">
          物理発送が終わった注文を「発送済み」として確定します。
          <strong>Shopify と Shopee台湾</strong>は、ここで初めてプラットフォームに
          追跡番号を送信し発送通知（お客様メール）が出ます。
          <strong>Shopee(台湾以外) / Lazada / Joom / momo</strong> はラベル発行時に
          通知済みのため、ここでは Wonect 内のステータスを締めるだけです。
        </p>
      </div>

      <div className="card space-y-4">
        <div className="flex flex-wrap gap-4 items-center justify-between">
          <span className="text-sm text-gray-500">
            実績取込済み（allocated）の注文が対象です。
            発送が終わった注文に<strong>チェック</strong>を入れて実行してください。
          </span>
          <button
            className="btn-primary"
            disabled={
              (selected.size === 0 && !includeShopeeTWUnallocated) ||
              mut.isPending
            }
            onClick={() => {
              if (mut.isPending) return;
              // 確認メッセージは選択中オプションで分岐
              const lines: string[] = [];
              if (selected.size > 0) {
                lines.push(`${selected.size} 件を「発送完了」にします。`);
              }
              if (includeShopeeTWUnallocated) {
                lines.push(
                  "**Shopee TW で未発送 (status != SHIPPED) の全注文** を" +
                  " Shopee 管理画面で発送済みに更新します:"
                );
                lines.push(
                  "・ 追跡番号未取得の注文は ECMS で先行発番されます (在庫入荷後の" +
                  "物理発送時にそのまま再利用)"
                );
                lines.push(
                  "・ Wonect 側の注文ステータスは変更しません" +
                  " (NEW / LABEL_READY / ALLOCATED のまま保持)"
                );
                lines.push(
                  "・ Shopee 側でキャンセルされる前に shipped 化することで、" +
                  "梱包後のキャンセル事故を防ぎます"
                );
              }
              if (skipShopeeNotify) {
                lines.push(
                  "**Shopee 通知をスキップ** モードで実行します:" +
                  " 選択した注文に対してプラットフォーム API を呼ばず、" +
                  "Wonect 側のステータスのみ SHIPPED に更新します。" +
                  "(在庫入荷後の物理発送時、既に Shopee 側 shipped の注文用)"
                );
              }
              if (!skipShopeeNotify && selected.size > 0) {
                lines.push("");
                lines.push("各プラットフォームへの影響:");
                lines.push(
                  "・Shopify / Shopee台湾: プラットフォームに追跡番号を送信し、" +
                  "発送済みに更新 + お客様に発送通知メールが届きます。"
                );
                lines.push(
                  "・Shopee(台湾以外) / Lazada / Joom / momo: ラベル発行時に" +
                  "通知済みのため、Wonect 内で発送済みに更新するのみ。"
                );
              }
              lines.push("");
              lines.push("この操作は取り消せません。");
              lines.push("物理発送が完了していることを確認してから実行してください。");
              lines.push("");
              lines.push("本当に実行しますか？");
              const ok = window.confirm(lines.join("\n"));
              if (!ok) return;
              mut.mutate();
            }}
          >
            {mut.isPending
              ? "処理中…"
              : includeShopeeTWUnallocated && selected.size === 0
                ? "Shopee TW 全件を Shopee 側で shipped 化"
                : `${selected.size} 件を発送完了にする`}
          </button>
        </div>

        {/* 拡張オプション (Kota さん要望 2026-06-12) */}
        <div className="border-t pt-3 space-y-2">
          <p className="text-xs font-semibold text-gray-600">
            拡張オプション (通常は使いません)
          </p>
          <label className="flex items-start gap-2 text-sm cursor-pointer">
            <input
              type="checkbox"
              className="mt-0.5"
              checked={includeShopeeTWUnallocated}
              onChange={(e) => setIncludeShopeeTWUnallocated(e.target.checked)}
            />
            <span>
              <strong>Shopee TW の未引き当て注文も Shopee 側で shipped 化する</strong>
              <span className="block text-xs text-gray-500">
                Shopee TW で <code>status != SHIPPED</code> の全 Order を対象に、
                追跡番号未取得なら ECMS で先行発番 → Shopee に shipped 通知。
                Wonect 側の注文ステータスは変更しません (在庫入荷後に物理発送するため)。
                <strong className="text-amber-700">
                  Shopee 側でキャンセルされる前に shipped 化する用途です。
                </strong>
              </span>
            </span>
          </label>
          <label className="flex items-start gap-2 text-sm cursor-pointer">
            <input
              type="checkbox"
              className="mt-0.5"
              checked={skipShopeeNotify}
              onChange={(e) => setSkipShopeeNotify(e.target.checked)}
            />
            <span>
              <strong>Shopee 通知をスキップ (Wonect 側だけ shipped 化)</strong>
              <span className="block text-xs text-gray-500">
                選択した注文に対して、プラットフォーム API を呼ばず Wonect 側
                ステータスだけ SHIPPED にします。
                <strong className="text-amber-700">
                  既に「Shopee TW を Shopee 側で先行 shipped 化」した注文を、
                  在庫入荷後に物理発送したときに使います。
                </strong>
              </span>
            </span>
          </label>
        </div>
      </div>

      <div className="card overflow-x-auto">
        {ordersQ.isLoading && <p className="text-gray-500">読み込み中…</p>}
        {ordersQ.data && (
          <>
            <div className="flex items-center justify-between mb-3">
              <span className="text-sm text-gray-500">
                {ordersQ.data.total} 件中 {ordersQ.data.orders.length} 件を表示
              </span>
              <button className="btn-secondary text-xs" onClick={toggleAll}>
                {selected.size === ordersQ.data.orders.filter((o) => o.tracking_no).length
                  ? "全選択解除"
                  : "全選択"}
              </button>
            </div>
            <table className="min-w-full text-sm">
              <thead className="border-b text-left text-gray-500">
                <tr>
                  <th className="w-8"></th>
                  <th className="py-2 pr-4">プラットフォーム</th>
                  <th className="py-2 pr-4">注文番号</th>
                  <th className="py-2 pr-4">追跡番号</th>
                  <th className="py-2 pr-4">受取人</th>
                  <th className="py-2">注文日</th>
                </tr>
              </thead>
              <tbody>
                {ordersQ.data.orders.length === 0 && (
                  <tr>
                    <td colSpan={6} className="py-6 text-center text-gray-400">
                      対象注文がありません
                    </td>
                  </tr>
                )}
                {ordersQ.data.orders.map((o: OrderDto) => (
                  <tr key={o.id} className="border-b last:border-0 hover:bg-gray-50">
                    <td className="py-2">
                      <input
                        type="checkbox"
                        disabled={!o.tracking_no}
                        checked={selected.has(o.id)}
                        onChange={() => toggle(o.id)}
                      />
                    </td>
                    <td className="py-2 pr-4">
                      <span className="font-medium">{o.platform}</span>
                      <span className="ml-1 text-xs text-gray-500">/{o.country_code}</span>
                    </td>
                    <td className="py-2 pr-4 font-mono text-xs">{o.order_no}</td>
                    <td className="py-2 pr-4 font-mono text-xs">
                      {o.tracking_no ?? (
                        <span className="text-amber-600">未取得</span>
                      )}
                    </td>
                    <td className="py-2 pr-4 truncate max-w-xs">{o.recipient_name}</td>
                    <td className="py-2 text-xs text-gray-500">
                      {o.order_date ? new Date(o.order_date).toLocaleString("ja-JP") : "-"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </>
        )}
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 p-4 rounded">
          {error}
        </div>
      )}

      {result && (
        <div className="card space-y-3">
          <h3 className="text-lg font-semibold">送信結果</h3>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3 text-sm">
            <div className="stat-card">
              <span className="text-gray-500">合計</span>
              <span className="text-2xl font-bold">{result.summary.total}</span>
            </div>
            <div className="stat-card">
              <span className="text-gray-500">成功</span>
              <span className="text-2xl font-bold text-green-600">{result.summary.success}</span>
            </div>
            {(result.summary.pre_shipped ?? 0) > 0 && (
              <div className="stat-card">
                <span className="text-gray-500">事前 shipped (TW)</span>
                <span className="text-2xl font-bold text-blue-600">
                  {result.summary.pre_shipped}
                </span>
              </div>
            )}
            <div className="stat-card">
              <span className="text-gray-500">失敗</span>
              <span className="text-2xl font-bold text-brand-alert">{result.summary.failed}</span>
            </div>
            <div className="stat-card">
              <span className="text-gray-500">スキップ</span>
              <span className="text-2xl font-bold text-gray-400">{result.summary.skipped}</span>
            </div>
          </div>
          <table className="min-w-full text-sm">
            <thead className="border-b text-left text-gray-500">
              <tr>
                <th className="py-2 pr-4">注文ID</th>
                <th className="py-2 pr-4">プラットフォーム</th>
                <th className="py-2 pr-4">結果</th>
                <th className="py-2">メッセージ</th>
              </tr>
            </thead>
            <tbody>
              {result.results.map((r) => (
                <tr key={r.order_id} className="border-b last:border-0">
                  <td className="py-2 pr-4 font-mono text-xs">{r.order_id.slice(0, 8)}…</td>
                  <td className="py-2 pr-4">{r.platform ?? "-"}</td>
                  <td className="py-2 pr-4">
                    <span
                      className={`px-2 py-0.5 rounded text-xs ${
                        r.status === "success"
                          ? "bg-green-100 text-green-800"
                          : r.status === "pre_shipped"
                            ? "bg-blue-100 text-blue-800"
                            : r.status === "failed"
                              ? "bg-red-100 text-red-800"
                              : "bg-gray-100 text-gray-700"
                      }`}
                    >
                      {r.status === "pre_shipped" ? "事前 shipped" : r.status}
                    </span>
                    {r.action && (
                      <span className="ml-1 text-xs text-gray-500">
                        ({r.action})
                      </span>
                    )}
                  </td>
                  <td className="py-2 text-xs text-gray-600">
                    {r.error ?? r.message ?? r.would_call ?? "-"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
